import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, User, Phone, Mail, Lock, ArrowRight, CheckCircle2, Globe, Zap, ShieldCheck, Store } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import UniversalNavbar from './UniversalNavbar';
import VFooter from './VFooter';
import API_BASE_URL from '../config/api';
import { ORANGE, ORANGE_DARK, INK, MUTED, PAPER, CREAM, DISPLAY, BODY } from '../utils/brand';
import { devLog } from '../utils/devLog';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    contact_number: '',
    email: '',
    password: '',
    confirm_password: '',
    termsAccepted: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Sanitize brand username as the user types: lowercase, allow a-z, 0-9 and dashes only.
  const sanitizeSlug = (v) =>
    v.toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/-{2,}/g, '-').slice(0, 30);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : name === 'username' ? sanitizeSlug(value) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errs = {};
    const { username, email, contact_number, password, confirm_password, termsAccepted } = formData;

    if (!username || username.length < 3) {
      errs.username = 'Brand username must be at least 3 characters (letters, numbers, dashes).';
    } else if (!/^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/.test(username)) {
      errs.username = 'Use lowercase letters, numbers, and dashes only. No spaces or special characters.';
    }

    if (!email) {
      errs.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = 'Please enter a valid email address.';
    }

    if (!contact_number) {
      errs.contact_number = 'Contact number is required.';
    } else if (!/^\d{10}$/.test(contact_number)) {
      errs.contact_number = 'Phone number must be exactly 10 digits.';
    }

    if (!password || password.length < 8) {
      errs.password = 'Password must be at least 8 characters.';
    }

    if (password !== confirm_password) {
      errs.confirm_password = 'Passwords do not match.';
    }

    if (!termsAccepted) {
      errs.termsAccepted = 'Please accept the terms and conditions.';
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      const firstErrorField = Object.keys(fieldErrors)[0] || 'username';
      const el = document.getElementById(firstErrorField);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setErrorMessage('Please fix the highlighted fields.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/register/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Your store is live!',
          html: `
            <div class="text-center">
              <p class="text-lg mb-3">${data.message}</p>
              <div class="p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border-2 border-orange-200 mb-3">
                <p class="text-sm text-gray-600 mb-1">Your store is now live at:</p>
                <p class="text-xl font-bold text-orange-600">
                  https://${formData.username}.vasstr.com
                </p>
              </div>
              <p class="text-sm text-gray-500">Login to start adding your products!</p>
            </div>
          `,
          confirmButtonText: 'Go to Login',
          confirmButtonColor: '#d25614',
          width: '32em',
        }).then(() => {
          window.location.href = '/login';
        });
      } else {
        setErrorMessage(data.error);
      }
    } catch (error) {
      devLog.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const itemVariants = {
    hidden: { y: 24, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  };

  const benefits = [
    { Icon: Globe, t: 'Your own branded URL', d: 'yourname.vasstr.com' },
    { Icon: Zap, t: 'Live in under 30 min', d: 'No code, no developer' },
    { Icon: ShieldCheck, t: 'Free to start', d: '14-day free trial' },
    { Icon: Store, t: 'Keep 97% of sales', d: 'No marketplace cut' },
  ];

  // Shared field styling
  const fieldWrap = 'flex items-center gap-3 rounded-2xl border bg-white px-4 transition-all duration-200';
  const fieldStyle = (hasError) => ({ borderColor: hasError ? '#fca5a5' : '#e7e1d8', height: 52 });
  const inputCls = 'w-full bg-transparent text-[15px] outline-none placeholder:text-gray-400';
  const labelCls = 'mb-1.5 block text-[13px] font-bold';
  const errCls = 'mt-1.5 text-[12.5px] font-semibold';

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ fontFamily: BODY, backgroundColor: PAPER, color: INK }}>
      <UniversalNavbar />

      <div className="relative px-4 pt-24 pb-16 sm:px-6 sm:pt-28 lg:px-8">
        {/* gradient mesh */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
          style={{ background: `radial-gradient(55% 60% at 50% 0%, ${ORANGE}12, transparent 70%)` }}
        />

        <div className="relative mx-auto max-w-2xl">
          {/* Hero */}
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <span
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-bold"
              style={{ backgroundColor: `${ORANGE}16`, color: ORANGE_DARK }}
            >
              <Zap className="h-3.5 w-3.5" /> Launch your online store free
            </span>
            <h1 className="mx-auto mt-5 max-w-xl text-[2.3rem] font-extrabold leading-[1.04] tracking-tight sm:text-[3rem]" style={{ fontFamily: DISPLAY, color: INK }}>
              Create your store in minutes
            </h1>
            <p className="mx-auto mt-4 max-w-lg text-[16px]" style={{ color: MUTED }}>
              Get a branded store at <span className="font-bold" style={{ color: ORANGE }}>yourname.vasstr.com</span> —
              manage products, orders &amp; customers, all in one place.
            </p>
          </motion.div>

          {/* Benefits strip */}
          <motion.div
            className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
          >
            {benefits.map((b) => (
              <motion.div
                key={b.t}
                variants={itemVariants}
                className="rounded-2xl bg-white p-4 text-center"
                style={{ boxShadow: '0 12px 30px -22px rgba(0,0,0,0.25)' }}
              >
                <span className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl" style={{ backgroundColor: `${ORANGE}14` }}>
                  <b.Icon className="h-5 w-5" style={{ color: ORANGE }} />
                </span>
                <p className="text-[12.5px] font-extrabold" style={{ color: INK }}>{b.t}</p>
                <p className="text-[11px] font-semibold" style={{ color: MUTED }}>{b.d}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Form card */}
          <motion.div
            className="mt-8 rounded-3xl bg-white p-6 sm:p-9"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            style={{ boxShadow: '0 30px 70px -35px rgba(0,0,0,0.28)' }}
          >
            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 rounded-2xl px-4 py-3 text-[13.5px] font-semibold"
                style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
                role="alert"
              >
                {errorMessage}
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Section 1 — Brand details */}
              <div>
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl text-[14px] font-bold text-white" style={{ backgroundColor: ORANGE }}>1</span>
                  <div>
                    <h3 className="text-[16px] font-extrabold" style={{ color: INK }}>Brand details</h3>
                    <p className="text-[12.5px]" style={{ color: MUTED }}>Create your unique store identity</p>
                  </div>
                </div>

                <div className="space-y-5">
                  {/* Username */}
                  <div>
                    <label htmlFor="username" className={labelCls} style={{ color: INK }}>Choose your brand username</label>
                    <div className={fieldWrap} style={fieldStyle(fieldErrors.username)}>
                      <User size={18} style={{ color: MUTED }} />
                      <input
                        type="text"
                        name="username"
                        id="username"
                        placeholder="e.g. mybrand, fashionstore"
                        value={formData.username}
                        onChange={handleChange}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck="false"
                        maxLength={30}
                        required
                        className={inputCls}
                        style={{ color: INK }}
                      />
                    </div>
                    {fieldErrors.username && <p className={errCls} style={{ color: '#dc2626' }}>{fieldErrors.username}</p>}
                    <AnimatePresence>
                      {formData.username && !fieldErrors.username && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="mt-3 overflow-hidden"
                        >
                          <div className="flex items-center gap-2 rounded-2xl px-4 py-3" style={{ backgroundColor: CREAM }}>
                            <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: ORANGE }} />
                            <span className="text-[13px] font-semibold" style={{ color: INK }}>
                              Your store will be live at{' '}
                              <span className="font-bold break-all" style={{ color: ORANGE_DARK }}>https://{formData.username}.vasstr.com</span>
                            </span>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Phone + Email */}
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact_number" className={labelCls} style={{ color: INK }}>Contact number</label>
                      <div className={fieldWrap} style={fieldStyle(fieldErrors.contact_number)}>
                        <Phone size={18} style={{ color: MUTED }} />
                        <input
                          type="tel"
                          name="contact_number"
                          id="contact_number"
                          placeholder="10-digit mobile"
                          value={formData.contact_number}
                          onChange={handleChange}
                          autoComplete="tel"
                          inputMode="numeric"
                          maxLength={10}
                          required
                          className={inputCls}
                          style={{ color: INK }}
                        />
                      </div>
                      {fieldErrors.contact_number && <p className={errCls} style={{ color: '#dc2626' }}>{fieldErrors.contact_number}</p>}
                    </div>
                    <div>
                      <label htmlFor="email" className={labelCls} style={{ color: INK }}>Email address</label>
                      <div className={fieldWrap} style={fieldStyle(fieldErrors.email)}>
                        <Mail size={18} style={{ color: MUTED }} />
                        <input
                          type="email"
                          name="email"
                          id="email"
                          placeholder="you@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          autoComplete="email"
                          required
                          className={inputCls}
                          style={{ color: INK }}
                        />
                      </div>
                      {fieldErrors.email && <p className={errCls} style={{ color: '#dc2626' }}>{fieldErrors.email}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2 — Security */}
              <div>
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl text-[14px] font-bold text-white" style={{ backgroundColor: INK }}>2</span>
                  <div>
                    <h3 className="text-[16px] font-extrabold" style={{ color: INK }}>Security</h3>
                    <p className="text-[12.5px]" style={{ color: MUTED }}>Secure your account</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="password" className={labelCls} style={{ color: INK }}>Create password</label>
                    <div className={fieldWrap} style={fieldStyle(fieldErrors.password)}>
                      <Lock size={18} style={{ color: MUTED }} />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        id="password"
                        placeholder="Min 8 characters"
                        value={formData.password}
                        onChange={handleChange}
                        autoComplete="new-password"
                        required
                        className={inputCls}
                        style={{ color: INK }}
                      />
                      <button type="button" onClick={() => setShowPassword((v) => !v)} className="text-gray-400 transition-colors hover:text-gray-600" aria-label="Toggle password">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {fieldErrors.password && <p className={errCls} style={{ color: '#dc2626' }}>{fieldErrors.password}</p>}
                  </div>
                  <div>
                    <label htmlFor="confirm_password" className={labelCls} style={{ color: INK }}>Confirm password</label>
                    <div className={fieldWrap} style={fieldStyle(fieldErrors.confirm_password)}>
                      <Lock size={18} style={{ color: MUTED }} />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirm_password"
                        id="confirm_password"
                        placeholder="Re-enter password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        autoComplete="new-password"
                        required
                        className={inputCls}
                        style={{ color: INK }}
                      />
                      <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} className="text-gray-400 transition-colors hover:text-gray-600" aria-label="Toggle password">
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {fieldErrors.confirm_password && <p className={errCls} style={{ color: '#dc2626' }}>{fieldErrors.confirm_password}</p>}
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div>
                <label htmlFor="termsAccepted" className="flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3.5" style={{ borderColor: fieldErrors.termsAccepted ? '#fca5a5' : '#e7e1d8' }}>
                  <input
                    type="checkbox"
                    id="termsAccepted"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    required
                    className="mt-0.5 h-5 w-5 flex-shrink-0 rounded"
                    style={{ accentColor: ORANGE }}
                  />
                  <span className="text-[13.5px] font-semibold" style={{ color: INK }}>
                    I agree to the{' '}
                    <a href="/TermsNConditions" className="font-bold underline-offset-2 hover:underline" style={{ color: ORANGE }}>terms and conditions</a>.
                    <span className="mt-0.5 block text-[12px] font-medium" style={{ color: MUTED }}>By signing up you create your own independent store website.</span>
                  </span>
                </label>
                {fieldErrors.termsAccepted && <p className={errCls} style={{ color: '#dc2626' }}>{fieldErrors.termsAccepted}</p>}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-[15.5px] font-bold text-white transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
                style={{ backgroundColor: ORANGE, boxShadow: `0 16px 34px -12px ${ORANGE}` }}
              >
                {isLoading ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    Launching your store…
                  </>
                ) : (
                  <>
                    Launch my store free <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>

              <div className="flex items-center gap-3">
                <span className="h-px flex-1" style={{ backgroundColor: '#ece6dd' }} />
                <span className="text-[12.5px] font-semibold" style={{ color: MUTED }}>Already have an account?</span>
                <span className="h-px flex-1" style={{ backgroundColor: '#ece6dd' }} />
              </div>

              <Link
                to="/login"
                className="flex w-full items-center justify-center gap-2 rounded-full border px-6 py-3.5 text-[15px] font-bold transition-colors hover:bg-black/[0.03]"
                style={{ color: INK, borderColor: '#e0d9cf' }}
              >
                Sign in to your account
              </Link>
            </form>
          </motion.div>

          <p className="mt-6 text-center text-[13px]" style={{ color: MUTED }}>
            Need help?{' '}
            <a href="mailto:team@vasstr.com" className="font-semibold transition-opacity hover:opacity-70" style={{ color: ORANGE }}>team@vasstr.com</a>{' '}
            · +91 97043 87878
          </p>
        </div>
      </div>

      <VFooter />
    </div>
  );
};

export default RegistrationForm;
